import { useElementSize } from '@/hooks/useElementSize';
import { FIT_LAYOUT_MEDIA_QUERY, useMediaQuery } from '@/hooks/useMediaQuery';
import { DashboardPanel } from './DashboardPanel';
import { PriceDistributionChart } from './PriceDistributionChart';
import { RailSkeleton } from './RailSkeleton';
import { RailStateBlock } from './RailStateBlock';
import { formatSessionDateLabel } from './dashboardFormat';
import { selectDistributionWindow } from './railCharts.utils';
import type { RailPanelState } from './railPanelState.utils';

interface PriceDistributionPanelProps {
  state: RailPanelState;
  sessionDate: string;
  onRetry: () => void;
}

export function PriceDistributionPanel({
  state,
  sessionDate,
  onRetry,
}: PriceDistributionPanelProps) {
  const fits = useMediaQuery(FIT_LAYOUT_MEDIA_QUERY);
  const [boxRef, boxSize] = useElementSize<HTMLDivElement>();
  const rows =
    state.kind === 'ready'
      ? selectDistributionWindow(state.analytics.priceDistribution, state.analytics.currentPrice)
      : [];

  return (
    <DashboardPanel
      title="Price Distribution"
      info="Share of the session spent at each strike. GEX = Gamma Exposure, VEX = Volatility Exposure."
      description={`Time Spent % at Each Strike (${formatSessionDateLabel(sessionDate)})`}
      className="fit:flex fit:flex-[1_0_300px] fit:flex-col"
    >
      {state.kind === 'error' ? (
        <RailStateBlock
          tone="error"
          title="Stock aggregates unavailable"
          message={state.message}
          onRetry={onRetry}
        />
      ) : state.kind === 'ready' && rows.length === 0 ? (
        <RailStateBlock
          title="No completed session"
          message={`No regular-hours price distribution for ${formatSessionDateLabel(sessionDate)}. Pick another day.`}
        />
      ) : (
        <>
          <div ref={boxRef} className="relative mt-1.5 fit:min-h-0 fit:flex-[1_1_0]">
            {state.kind === 'loading' ? (
              <RailSkeleton className="mt-3.5 h-[420px] fit:mt-0 fit:h-full" />
            ) : boxSize.width > 0 ? (
              <div className={fits ? 'absolute inset-0' : undefined}>
                <PriceDistributionChart
                  rows={rows}
                  currentPrice={state.analytics.currentPrice}
                  width={boxSize.width}
                  fillHeight={fits ? boxSize.height : 0}
                />
              </div>
            ) : null}
          </div>
          {state.kind === 'ready' && (
            <div className="mt-1 flex justify-center gap-5 rounded border border-line px-2.5 py-1.5 text-xs text-softer">
              <span className="inline-flex items-center gap-2">
                <i className="h-[13px] w-[13px] rounded-[2px] bg-bar" />% Time Spent
              </span>
              {state.analytics.currentPrice != null && (
                <span className="inline-flex items-center gap-2">
                  <i className="w-[18px] border-t-2 border-dashed border-gold" />
                  {state.analytics.priceLabel}
                </span>
              )}
            </div>
          )}
        </>
      )}
    </DashboardPanel>
  );
}
